import { TourDto } from "components/tours/types";
import React, { useMemo } from "react";
import {
    EVENT_ROW_SPAN,
    HOURS,
    SLOTS_PER_HOUR,
    SUBROWS_PER_SLOT,
    computeOverlapLayout,
    formatDateKey,
    formatTime,
    isSameDay,
    parseTimeToRow,
} from "./calendar-utils";

interface DayGridProps {
    day: Date;
    today: Date;
    toursByDate: Map<string, TourDto[]>;
    onTourClick: (tour: TourDto) => void;
}

const DAY_ABBREVS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

export const DayGrid: React.FC<DayGridProps> = ({
    day,
    today,
    toursByDate,
    onTourClick,
}) => {
    const dateKey = formatDateKey(day);
    const isToday = isSameDay(day, today);

    const { allDayTours, timedTours } = useMemo(() => {
        const dayTours = toursByDate.get(dateKey) || [];
        const allDay: TourDto[] = [];
        const timed: TourDto[] = [];
        for (const tour of dayTours) {
            if (tour.tourTime) {
                timed.push(tour);
            } else {
                allDay.push(tour);
            }
        }
        return { allDayTours: allDay, timedTours: timed };
    }, [toursByDate, dateKey]);

    const laidOutTours = useMemo(() => {
        const withRows = timedTours
            .map(tour => ({
                event: tour,
                row: parseTimeToRow(tour.tourTime),
            }))
            .filter(
                (
                    item,
                ): item is {
                    event: TourDto;
                    row: number;
                } => item.row !== null,
            );
        return computeOverlapLayout(withRows);
    }, [timedTours]);

    return (
        <div className="isolate flex flex-auto flex-col overflow-auto bg-white border border-gray-200 rounded-lg">
            <div className="sticky top-0 z-30 bg-white border-b border-gray-200">
                <div className="flex items-center">
                    <div className="w-14 flex-none flex flex-col items-center py-2">
                        <span className="text-xs font-medium text-gray-500">
                            {DAY_ABBREVS[day.getDay()]}
                        </span>
                        <span
                            className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-full text-lg font-semibold ${
                                isToday ? "bg-brand-600 text-white" : "text-gray-900"
                            }`}>
                            {day.getDate()}
                        </span>
                    </div>
                    {allDayTours.length > 0 && (
                        <div className="flex-1 min-w-0 py-1 pr-2 space-y-1">
                            {allDayTours.map(tour => (
                                <button
                                    key={tour.id}
                                    onClick={() => onTourClick(tour)}
                                    className="block w-full text-left px-2 py-1.5 text-sm rounded bg-brand-600 text-white hover:bg-brand-700 truncate">
                                    {tour.communityName}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex max-w-full flex-none flex-col">
                <div className="flex flex-auto">
                    <div className="sticky left-0 z-10 w-14 flex-none bg-white ring-1 ring-gray-100" />
                    <div className="grid flex-auto grid-cols-1 grid-rows-1 min-w-0">
                        <div
                            style={{
                                gridTemplateRows: `repeat(${HOURS.length * SLOTS_PER_HOUR}, minmax(1.5rem, 1fr))`,
                            }}
                            className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100">
                            <div className="row-end-1 h-7" />
                            {HOURS.map((hour, i) => (
                                <React.Fragment key={i}>
                                    <div>
                                        <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs/5 text-gray-400">
                                            {hour}
                                        </div>
                                    </div>
                                    <div />
                                </React.Fragment>
                            ))}
                        </div>

                        <ol
                            style={{
                                gridTemplateRows: `1.75rem repeat(${HOURS.length * SLOTS_PER_HOUR * SUBROWS_PER_SLOT}, minmax(0, 1fr)) auto`,
                            }}
                            className="col-start-1 col-end-2 row-start-1 z-10 grid grid-cols-1 pr-2">
                            {laidOutTours.map(({ event: tour, layout }) => {
                                const widthPct = 100 / layout.totalColumns;
                                const leftPct = layout.columnIndex * widthPct;
                                return (
                                    <li
                                        key={tour.id}
                                        style={{
                                            gridRow: `${layout.row} / span ${EVENT_ROW_SPAN}`,
                                            gridColumnStart: 1,
                                        }}
                                        className="relative mt-px pointer-events-none">
                                        <button
                                            onClick={() => onTourClick(tour)}
                                            style={{
                                                left: `${leftPct}%`,
                                                width: `${widthPct}%`,
                                            }}
                                            className="pointer-events-auto group absolute inset-y-px flex flex-col overflow-hidden rounded-md bg-brand-600 p-2 text-sm hover:bg-brand-700 text-left">
                                            <p className="font-semibold text-white">
                                                {tour.communityName}
                                            </p>
                                            <p className="text-brand-100">
                                                {formatTime(tour.tourTime)}
                                            </p>
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    );
};
